import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { formatBRL, formatDateBR } from "../../shared/ui/format";
import { formatDateInHoursAndMinutes } from "../../utils/date";
import ReasonDialog from "./ReasonDialog";
import ReasonPicker from "./ReasonPicker";
import { useReasonCapture } from "./useReasonCapture";
import { ReasonCapture } from "./types";

export type RefusalVacancyRow = {
  id: number;
  date?: string | null;
  hospital_name?: string | null;
  start_hour?: string | null;
  end_hour?: string | null;
  real_net_value?: number | string | null;
};

export type RefusalDialogProps = {
  context?: string;
  /** Plantões marcados "Não antecipar": só leitura (P12, sem checkbox por plantão). */
  rows: RefusalVacancyRow[];
  /** Todos os plantões marcados: o pacote é encerrado como recusado (B1a). */
  total: boolean;
  busy: boolean;
  error?: string | null;
  /** `label` = rótulo do motivo escolhido (para o resumo do envio). */
  onConfirm: (refusal: ReasonCapture, label: string) => void;
  onClose: () => void;
};

// Mesmo critério da tabela da Conferência: converte para o fuso do navegador (o BE manda ISO em UTC).
const hourOf = (value?: string | null) => (value ? formatDateInHoursAndMinutes(new Date(value)) : "");
const planned = (row: RefusalVacancyRow) => [hourOf(row.start_hour), hourOf(row.end_hour)].filter(Boolean).join(" – ") || "—";

/** Janela (b): motivo da recusa ("Não antecipar") na Conferência da empresa, no envio (CA-F1a-03/17). */
export default function RefusalDialog({ context, rows, total, busy, error, onConfirm, onClose }: RefusalDialogProps) {
  const capture = useReasonCapture("refusal");
  return (
    <ReasonDialog
      title="Não antecipar"
      context={context}
      destructive
      confirmLabel={total ? "Recusar pacote" : "Confirmar “Não antecipar”"}
      confirmIcon={<RemoveCircleOutlineIcon />}
      busy={busy}
      error={error}
      valid={capture.valid}
      onConfirm={() => capture.valid && onConfirm(capture.toCapture(), capture.chosen[0]?.label ?? "")}
      onClose={onClose}
    >
      {total && (
        <Alert severity="warning">
          <b>Todos os plantões estão como “Não antecipar”. O pacote será encerrado como recusado</b>: nenhum plantão segue para o Antecipe e o médico
          verá o pedido como Recusado. Esta ação não pode ser desfeita.
        </Alert>
      )}
      <ReasonPicker capture={capture} label="Motivo da recusa" />
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 500, mb: 0.5 }}>Plantões que não serão antecipados</Typography>
        <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: "8px", maxHeight: 260 }}>
          <Table size="small" stickyHeader aria-label="Plantões que não serão antecipados">
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Unidade</TableCell>
                <TableCell>Previsto</TableCell>
                <TableCell align="right">Valor proporcional</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{formatDateBR(row.date, "—")}</TableCell>
                  <TableCell>{row.hospital_name || "—"}</TableCell>
                  <TableCell>{planned(row)}</TableCell>
                  <TableCell align="right">{formatBRL(row.real_net_value)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {!total && (
          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
            Estes plantões ficam ligados ao motivo e vão para um novo pacote; o médico vê o pedido deles como Recusado.
          </Typography>
        )}
      </Box>
    </ReasonDialog>
  );
}
